import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { createWorker, type Worker } from 'tesseract.js';
import Modal from '../Modal';
import { DuplicateClientAlert } from '../DuplicateClientModal';
import { DuplicateClientError, type ExistingClientRef } from '../../api/clients';
import { SortableTh } from '../SortableTh';
import { useTableSortState } from '../../hooks/useTableSort';
import { sortRows } from '../../utils/tableSort';
import { parseBusinessCardText } from '../../utils/businessCardParser';
import {
  businessCardImageUrl,
  convertBusinessCardScan,
  createBusinessCardScan,
  deleteBusinessCardScan,
  emptyBusinessCardFields,
  fetchBusinessCardScans,
  updateBusinessCardScan,
  updateBusinessCardTags,
  type BusinessCardFields,
  type BusinessCardScan,
} from '../../api/businessCards';
import {
  LEAD_FIELD_TAG_SUGGESTIONS,
  LEAD_PRIORITY_TAGS,
  type WebsiteFormLeadTag,
} from '../../api/websiteFormLeads';

/** Labels for the editable contact fields, in the order they are shown. */
const FIELD_LABELS: { key: keyof BusinessCardFields; label: string; multiline?: boolean }[] = [
  { key: 'full_name', label: 'Name' },
  { key: 'job_title', label: 'Title' },
  { key: 'company', label: 'Company' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'website', label: 'Website' },
  { key: 'address', label: 'Address', multiline: true },
  { key: 'notes', label: 'Notes', multiline: true },
];

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function tagsOf(scan: BusinessCardScan): WebsiteFormLeadTag[] {
  return Array.isArray(scan.tags) ? scan.tags : [];
}

function priorityOf(scan: BusinessCardScan): string {
  return tagsOf(scan).find((t) => t.kind === 'priority')?.label ?? '';
}

function fieldTagsOf(scan: BusinessCardScan): string[] {
  return tagsOf(scan)
    .filter((t) => t.kind === 'field')
    .map((t) => t.label);
}

function fieldsOf(scan: BusinessCardScan): BusinessCardFields {
  return {
    full_name: scan.full_name ?? '',
    job_title: scan.job_title ?? '',
    company: scan.company ?? '',
    email: scan.email ?? '',
    phone: scan.phone ?? '',
    website: scan.website ?? '',
    address: scan.address ?? '',
    notes: scan.notes ?? '',
  };
}

/** Values the confirmation modal shows and the user may edit before saving. */
interface ConvertDraft {
  company: string;
  point_of_contact: string;
  contact_email: string;
  contact_phone: string;
  source: string;
  industry: string;
  notes: string;
}

function draftFromScan(scan: BusinessCardScan): ConvertDraft {
  const name = scan.full_name?.trim() ?? '';
  const noteParts = [
    scan.job_title?.trim() ? `Title: ${scan.job_title.trim()}` : '',
    scan.website?.trim() ? `Website: ${scan.website.trim()}` : '',
    scan.address?.trim() ? `Address: ${scan.address.trim()}` : '',
    scan.notes?.trim() ?? '',
  ].filter(Boolean);
  return {
    company: scan.company?.trim() || name,
    point_of_contact: name || scan.company?.trim() || '',
    contact_email: scan.email ?? '',
    contact_phone: scan.phone ?? '',
    source: 'Business Card',
    industry: fieldTagsOf(scan)[0] ?? '',
    notes: noteParts.join('\n'),
  };
}

interface Props {
  userId: number;
}

/**
 * Beta business card scanner: capture a card with the device camera, read it
 * on-device with OCR, confirm what it found, then triage it like any other
 * marketing lead.
 */
const BusinessCardScannerSection: React.FC<Props> = ({ userId }) => {
  const [scans, setScans] = useState<BusinessCardScan[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const { sortKey, sortDir, toggleSort } = useTableSortState();

  // Camera capture
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const workerRef = useRef<Worker | null>(null);
  const photoUrlRef = useRef<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [readingStatus, setReadingStatus] = useState('');

  // Confirmation of a fresh scan
  const [draft, setDraft] = useState<BusinessCardFields | null>(null);
  const [rawText, setRawText] = useState('');
  const [showRawText, setShowRawText] = useState(false);
  const [photo, setPhoto] = useState<Blob | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Detail / edit
  const [selected, setSelected] = useState<BusinessCardScan | null>(null);
  const [editFields, setEditFields] = useState<BusinessCardFields | null>(null);
  const [customTag, setCustomTag] = useState('');
  const [rowSaving, setRowSaving] = useState(false);
  const [rowError, setRowError] = useState('');

  // Convert to a client lead
  const [converting, setConverting] = useState<BusinessCardScan | null>(null);
  const [convertDraft, setConvertDraft] = useState<ConvertDraft | null>(null);
  const [convertEditing, setConvertEditing] = useState(false);
  const [convertSaving, setConvertSaving] = useState(false);
  const [convertError, setConvertError] = useState('');
  const [convertDuplicate, setConvertDuplicate] = useState<ExistingClientRef | null>(null);
  const [convertDone, setConvertDone] = useState<{ name: string; clientId: number } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      setScans(await fetchBusinessCardScans(userId));
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load business card scans.');
      setScans([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  // Free the camera, the OCR worker and the preview URL when we go away.
  useEffect(
    () => () => {
      stopCamera();
      void workerRef.current?.terminate();
      workerRef.current = null;
      if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current);
    },
    [stopCamera]
  );

  const showPhoto = useCallback((blob: Blob | null) => {
    if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current);
    photoUrlRef.current = blob ? URL.createObjectURL(blob) : null;
    setPhoto(blob);
    setPhotoUrl(photoUrlRef.current);
  }, []);

  const openCamera = async () => {
    setCameraError('');
    setCameraOpen(true);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('This browser will not share a camera with the site.');
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        // Rear camera on phones and tablets; falls back to whatever exists.
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => undefined);
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Could not start the camera.';
      setCameraError(
        `${message} A camera needs a secure (https) page and permission; you can upload a photo of the card instead.`
      );
    }
  };

  const closeCamera = () => {
    stopCamera();
    setCameraOpen(false);
  };

  /** Runs OCR, parses the result and opens the confirmation form. */
  const readCard = useCallback(
    async (blob: Blob) => {
      setReadingStatus('Starting the reader…');
      try {
        if (!workerRef.current) {
          workerRef.current = await createWorker('eng', 1, {
            logger: (m: { status?: string; progress?: number }) => {
              if (m.status === 'recognizing text') {
                setReadingStatus(`Reading the card… ${Math.round((m.progress ?? 0) * 100)}%`);
              } else if (m.status) {
                setReadingStatus(`${m.status}…`);
              }
            },
          });
        }
        setReadingStatus('Reading the card…');
        const { data } = await workerRef.current.recognize(blob);
        const text = data.text ?? '';
        setRawText(text);
        setDraft({ ...emptyBusinessCardFields(), ...parseBusinessCardText(text) });
        setShowRawText(false);
        setSaveError('');
      } catch (e) {
        // A failed read still lets them type the card in by hand.
        setRawText('');
        setDraft(emptyBusinessCardFields());
        setSaveError(
          e instanceof Error
            ? `Could not read the card automatically (${e.message}). Enter the details by hand.`
            : 'Could not read the card automatically. Enter the details by hand.'
        );
      } finally {
        setReadingStatus('');
      }
    },
    []
  );

  const capture = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      setCameraError('The camera is still warming up. Try again in a second.');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92)
    );
    if (!blob) {
      setCameraError('Could not capture a frame from the camera.');
      return;
    }
    closeCamera();
    showPhoto(blob);
    await readCard(blob);
  };

  const pickPhoto = async (file: File | undefined) => {
    if (!file) return;
    closeCamera();
    showPhoto(file);
    await readCard(file);
  };

  const setDraftValue = (key: keyof BusinessCardFields, value: string) =>
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));

  const discardDraft = () => {
    setDraft(null);
    setRawText('');
    setSaveError('');
    showPhoto(null);
  };

  const saveScan = async () => {
    if (!draft) return;
    setSaving(true);
    setSaveError('');
    try {
      const created = await createBusinessCardScan(userId, draft, rawText, photo);
      setScans((prev) => [created, ...prev]);
      discardDraft();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Failed to save the scan.');
    } finally {
      setSaving(false);
    }
  };

  const replaceScan = useCallback((updated: BusinessCardScan) => {
    setScans((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    setSelected((prev) => (prev && prev.id === updated.id ? updated : prev));
  }, []);

  const saveTags = async (scan: BusinessCardScan, tags: WebsiteFormLeadTag[]) => {
    setRowSaving(true);
    setRowError('');
    try {
      replaceScan(await updateBusinessCardTags(scan.id, userId, tags));
    } catch (e) {
      setRowError(e instanceof Error ? e.message : 'Failed to update tags.');
    } finally {
      setRowSaving(false);
    }
  };

  const togglePriority = (scan: BusinessCardScan, label: string) => {
    const others = tagsOf(scan).filter((t) => t.kind !== 'priority');
    const next: WebsiteFormLeadTag[] =
      priorityOf(scan) === label ? others : [...others, { kind: 'priority', label }];
    void saveTags(scan, next);
  };

  const toggleFieldTag = (scan: BusinessCardScan, label: string) => {
    const clean = label.trim();
    if (!clean) return;
    const current = tagsOf(scan);
    const has = current.some(
      (t) => t.kind === 'field' && t.label.toLowerCase() === clean.toLowerCase()
    );
    const next = has
      ? current.filter((t) => !(t.kind === 'field' && t.label.toLowerCase() === clean.toLowerCase()))
      : [...current, { kind: 'field' as const, label: clean }];
    void saveTags(scan, next);
  };

  const saveEdits = async () => {
    if (!selected || !editFields) return;
    setRowSaving(true);
    setRowError('');
    try {
      replaceScan(await updateBusinessCardScan(selected.id, userId, editFields));
      setEditFields(null);
    } catch (e) {
      setRowError(e instanceof Error ? e.message : 'Failed to save changes.');
    } finally {
      setRowSaving(false);
    }
  };

  const removeScan = async (scan: BusinessCardScan) => {
    if (!window.confirm('Delete this scan and its photo? This cannot be undone.')) return;
    setRowSaving(true);
    setRowError('');
    try {
      await deleteBusinessCardScan(scan.id, userId);
      setScans((prev) => prev.filter((s) => s.id !== scan.id));
      setSelected(null);
    } catch (e) {
      setRowError(e instanceof Error ? e.message : 'Failed to delete the scan.');
    } finally {
      setRowSaving(false);
    }
  };

  const openConvert = (scan: BusinessCardScan) => {
    setConverting(scan);
    setConvertDraft(draftFromScan(scan));
    setConvertEditing(false);
    setConvertError('');
    setConvertDuplicate(null);
  };

  const setConvertField = <K extends keyof ConvertDraft>(key: K, value: ConvertDraft[K]) =>
    setConvertDraft((prev) => (prev ? { ...prev, [key]: value } : prev));

  const confirmConvert = async () => {
    if (!converting || !convertDraft) return;
    setConvertSaving(true);
    setConvertError('');
    setConvertDuplicate(null);
    try {
      const result = await convertBusinessCardScan(converting.id, {
        user_id: userId,
        ...convertDraft,
      });
      replaceScan(result.scan);
      setConvertDone({ name: convertDraft.company, clientId: result.client_id });
      setConverting(null);
      setConvertDraft(null);
    } catch (e) {
      if (e instanceof DuplicateClientError) setConvertDuplicate(e.existing);
      else setConvertError(e instanceof Error ? e.message : 'Failed to add lead.');
    } finally {
      setConvertSaving(false);
    }
  };

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return scans;
    return scans.filter((s) =>
      [s.full_name, s.job_title, s.company, s.email, s.phone, s.website, s.address, s.notes]
        .concat(tagsOf(s).map((t) => t.label))
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term)
    );
  }, [scans, search]);

  const sorted = useMemo(
    () =>
      sortRows(visible, sortKey, sortDir, (s, key) => {
        switch (key) {
          case 'scanned':
            return s.created_at ?? '';
          case 'name':
            return s.full_name ?? '';
          case 'company':
            return s.company ?? '';
          case 'email':
            return s.email ?? '';
          case 'phone':
            return s.phone ?? '';
          case 'priority':
            return priorityOf(s);
          case 'client':
            return s.converted_company ?? '';
          default:
            return '';
        }
      }),
    [visible, sortKey, sortDir]
  );

  const renderTagPills = (scan: BusinessCardScan) => {
    const priority = priorityOf(scan);
    const fields = fieldTagsOf(scan);
    if (!priority && fields.length === 0) return <span className="website-lead-muted">—</span>;
    return (
      <span className="website-lead-tag-list">
        {priority ? (
          <span className={`website-lead-tag website-lead-tag--priority-${priority.toLowerCase()}`}>
            {priority}
          </span>
        ) : null}
        {fields.map((f) => (
          <span key={f} className="website-lead-tag">
            {f}
          </span>
        ))}
      </span>
    );
  };

  const unconvertedCount = scans.filter((s) => s.converted_client_id == null).length;

  return (
    <section className="events-table-section" aria-labelledby="business-card-heading">
      <div className="marketing-order-header">
        <div>
          <h2 className="events-section-title" id="business-card-heading">
            Business Card Scanner <span className="card-beta-pill">Beta</span>{' '}
            <span className="events-section-count">({scans.length})</span>
            {unconvertedCount > 0 ? (
              <span className="website-lead-new-badge">{unconvertedCount} not yet added</span>
            ) : null}
          </h2>
          <p className="page-subtitle website-lead-intro">
            Point the camera at a business card. The card is read on this device, so check what it
            found before saving — then tag it and add it to the client database.
          </p>
        </div>
        <button type="button" className="add-button marketing-order-new-btn" onClick={() => void openCamera()}>
          Scan a card
        </button>
      </div>

      {loadError ? (
        <p className="create-order-error" role="alert">
          {loadError}
        </p>
      ) : null}
      {rowError ? (
        <p className="create-order-error" role="alert">
          {rowError}
        </p>
      ) : null}
      {readingStatus ? (
        <p className="marketing-order-notice" role="status">
          {readingStatus}
        </p>
      ) : null}
      {convertDone ? (
        <p className="marketing-order-notice" role="status">
          {convertDone.name} was added to the client database and the sales team has been notified.{' '}
          <Link to={`/lead/${convertDone.clientId}`}>Open the lead</Link>.
        </p>
      ) : null}

      <div className="website-lead-controls">
        <input
          type="search"
          className="website-lead-search"
          placeholder="Search name, company, email, tags…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search business card scans"
        />
      </div>

      {loading ? (
        <p className="page-subtitle">Loading scans…</p>
      ) : (
        <div className="events-table-wrap">
          <table className="tasks-search-results-table website-lead-table">
            <thead>
              <tr>
                <SortableTh label="Scanned" columnKey="scanned" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Name" columnKey="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Company" columnKey="company" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Email" columnKey="email" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Phone" columnKey="phone" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Tags" columnKey="priority" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Actions" columnKey="client" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={7} className="events-empty-cell">
                    {scans.length === 0
                      ? 'No cards scanned yet. Use “Scan a card” to add the first one.'
                      : 'No scans match this search.'}
                  </td>
                </tr>
              ) : (
                sorted.map((s) => (
                  <tr
                    key={s.id}
                    className="events-table-row-clickable"
                    onClick={() => {
                      setSelected(s);
                      setEditFields(null);
                      setCustomTag('');
                      setRowError('');
                    }}
                  >
                    <td data-label="Scanned">{formatDateTime(s.created_at)}</td>
                    <td data-label="Name">{s.full_name || <span className="website-lead-muted">—</span>}</td>
                    <td data-label="Company">{s.company || <span className="website-lead-muted">—</span>}</td>
                    <td data-label="Email">{s.email || <span className="website-lead-muted">—</span>}</td>
                    <td data-label="Phone">{s.phone || <span className="website-lead-muted">—</span>}</td>
                    <td data-label="Tags">{renderTagPills(s)}</td>
                    <td data-label="Actions">
                      <div onClick={(e) => e.stopPropagation()}>
                        {s.converted_client_id != null ? (
                          <Link to={`/lead/${s.converted_client_id}`} className="website-lead-link">
                            View lead
                          </Link>
                        ) : (
                          <button
                            type="button"
                            className="website-lead-add-btn"
                            onClick={() => openConvert(s)}
                          >
                            Add to leads
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Live camera */}
      <Modal isOpen={cameraOpen} onClose={closeCamera} title="Scan a business card" wide>
        <div className="modal-body">
          {cameraError ? (
            <p className="create-order-error" role="alert">
              {cameraError}
            </p>
          ) : null}
          <div className="card-camera-frame">
            <video ref={videoRef} className="card-camera-video" playsInline muted autoPlay />
            <div className="card-camera-guide" aria-hidden="true" />
          </div>
          <p className="page-subtitle">
            Fill the frame with the card and hold steady. Good light and a flat card read best.
          </p>
          <label className="card-camera-upload">
            <span>No camera? Upload a photo of the card</span>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => void pickPhoto(e.target.files?.[0])}
            />
          </label>
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={closeCamera}>
            Cancel
          </button>
          <button type="button" className="update-button" onClick={() => void capture()}>
            Capture
          </button>
        </div>
      </Modal>

      {/* Confirm what OCR read before it is saved */}
      <Modal
        isOpen={draft != null}
        onClose={() => (saving ? undefined : discardDraft())}
        title="Check the card details"
        wide
      >
        {draft ? (
          <>
            <div className="modal-body">
              <p className="page-subtitle">
                These were read off the card automatically. Fix anything that came through wrong.
              </p>
              {saveError ? (
                <p className="create-order-error" role="alert">
                  {saveError}
                </p>
              ) : null}
              {photoUrl ? (
                <img src={photoUrl} alt="Captured business card" className="card-scan-preview" />
              ) : null}
              <div className="marketing-order-grid">
                {FIELD_LABELS.filter((f) => !f.multiline).map((f) => (
                  <label key={f.key} className="marketing-order-field">
                    <span>{f.label}</span>
                    <input
                      type="text"
                      value={draft[f.key]}
                      disabled={saving}
                      onChange={(e) => setDraftValue(f.key, e.target.value)}
                    />
                  </label>
                ))}
              </div>
              {FIELD_LABELS.filter((f) => f.multiline).map((f) => (
                <label key={f.key} className="marketing-order-field">
                  <span>{f.label}</span>
                  <textarea
                    rows={2}
                    value={draft[f.key]}
                    disabled={saving}
                    onChange={(e) => setDraftValue(f.key, e.target.value)}
                  />
                </label>
              ))}
              {rawText ? (
                <div className="card-raw-text">
                  <button
                    type="button"
                    className="website-lead-status-btn"
                    onClick={() => setShowRawText((v) => !v)}
                  >
                    {showRawText ? 'Hide raw text' : 'Show raw text'}
                  </button>
                  {showRawText ? <pre className="website-lead-message">{rawText}</pre> : null}
                </div>
              ) : null}
            </div>
            <div className="modal-actions">
              <button type="button" className="cancel-button" onClick={discardDraft} disabled={saving}>
                Discard
              </button>
              <button
                type="button"
                className="website-lead-status-btn"
                onClick={() => void openCamera()}
                disabled={saving}
              >
                Rescan
              </button>
              <button type="button" className="update-button" onClick={() => void saveScan()} disabled={saving}>
                {saving ? 'Saving…' : 'Save scan'}
              </button>
            </div>
          </>
        ) : null}
      </Modal>

      {/* One scan: photo, details, tags, conversion */}
      <Modal
        isOpen={selected != null}
        onClose={() => setSelected(null)}
        title={selected?.full_name || selected?.company || 'Business card'}
        wide
      >
        {selected ? (
          <>
            <div className="modal-body">
              {rowError ? (
                <p className="create-order-error" role="alert">
                  {rowError}
                </p>
              ) : null}
              {selected.image_file_name ? (
                <img
                  src={businessCardImageUrl(selected.id, userId)}
                  alt="Scanned business card"
                  className="card-scan-preview"
                />
              ) : null}

              <h4 className="website-lead-subheading">Details</h4>
              {editFields ? (
                <>
                  <div className="marketing-order-grid">
                    {FIELD_LABELS.filter((f) => !f.multiline).map((f) => (
                      <label key={f.key} className="marketing-order-field">
                        <span>{f.label}</span>
                        <input
                          type="text"
                          value={editFields[f.key]}
                          disabled={rowSaving}
                          onChange={(e) =>
                            setEditFields((prev) => (prev ? { ...prev, [f.key]: e.target.value } : prev))
                          }
                        />
                      </label>
                    ))}
                  </div>
                  {FIELD_LABELS.filter((f) => f.multiline).map((f) => (
                    <label key={f.key} className="marketing-order-field">
                      <span>{f.label}</span>
                      <textarea
                        rows={2}
                        value={editFields[f.key]}
                        disabled={rowSaving}
                        onChange={(e) =>
                          setEditFields((prev) => (prev ? { ...prev, [f.key]: e.target.value } : prev))
                        }
                      />
                    </label>
                  ))}
                  <div className="website-lead-status-actions">
                    <button
                      type="button"
                      className="update-button"
                      onClick={() => void saveEdits()}
                      disabled={rowSaving}
                    >
                      {rowSaving ? 'Saving…' : 'Save changes'}
                    </button>
                    <button
                      type="button"
                      className="website-lead-status-btn"
                      onClick={() => setEditFields(null)}
                      disabled={rowSaving}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <dl className="website-lead-details">
                    {FIELD_LABELS.map((f) => (
                      <div key={f.key}>
                        <dt>{f.label}</dt>
                        <dd>{fieldsOf(selected)[f.key] || '—'}</dd>
                      </div>
                    ))}
                    <div>
                      <dt>Scanned by</dt>
                      <dd>
                        {selected.scanned_by_username || '—'} · {formatDateTime(selected.created_at)}
                      </dd>
                    </div>
                  </dl>
                  <button
                    type="button"
                    className="website-lead-status-btn"
                    onClick={() => setEditFields(fieldsOf(selected))}
                  >
                    Edit details
                  </button>
                </>
              )}

              <h4 className="website-lead-subheading">Priority</h4>
              <div className="website-lead-tag-picker">
                {LEAD_PRIORITY_TAGS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    disabled={rowSaving}
                    className={`website-lead-tag-btn${
                      priorityOf(selected) === p ? ' website-lead-tag-btn--on' : ''
                    }`}
                    onClick={() => togglePriority(selected, p)}
                  >
                    {p}
                  </button>
                ))}
              </div>

              <h4 className="website-lead-subheading">Field</h4>
              <div className="website-lead-tag-picker">
                {Array.from(new Set([...LEAD_FIELD_TAG_SUGGESTIONS, ...fieldTagsOf(selected)])).map(
                  (f) => (
                    <button
                      key={f}
                      type="button"
                      disabled={rowSaving}
                      className={`website-lead-tag-btn${
                        fieldTagsOf(selected).some((t) => t.toLowerCase() === f.toLowerCase())
                          ? ' website-lead-tag-btn--on'
                          : ''
                      }`}
                      onClick={() => toggleFieldTag(selected, f)}
                    >
                      {f}
                    </button>
                  )
                )}
              </div>
              <div className="website-lead-tag-add">
                <input
                  type="text"
                  value={customTag}
                  placeholder="Add another field…"
                  onChange={(e) => setCustomTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      toggleFieldTag(selected, customTag);
                      setCustomTag('');
                    }
                  }}
                />
                <button
                  type="button"
                  disabled={rowSaving || !customTag.trim()}
                  className="website-lead-status-btn"
                  onClick={() => {
                    toggleFieldTag(selected, customTag);
                    setCustomTag('');
                  }}
                >
                  Add tag
                </button>
              </div>

              <h4 className="website-lead-subheading">Client database</h4>
              {selected.converted_client_id != null ? (
                <p className="website-lead-muted">
                  Added as a lead
                  {selected.converted_by_username ? ` by ${selected.converted_by_username}` : ''}.{' '}
                  <Link to={`/lead/${selected.converted_client_id}`}>Open the lead</Link>.
                </p>
              ) : (
                <button
                  type="button"
                  className="update-button"
                  onClick={() => {
                    const scan = selected;
                    setSelected(null);
                    openConvert(scan);
                  }}
                >
                  Add to client database
                </button>
              )}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="cancel-button"
                onClick={() => setSelected(null)}
                disabled={rowSaving}
              >
                Close
              </button>
              <button
                type="button"
                className="website-lead-status-btn marketing-danger-btn"
                onClick={() => void removeScan(selected)}
                disabled={rowSaving}
              >
                Delete scan
              </button>
            </div>
          </>
        ) : null}
      </Modal>

      {/* Confirmation before the scan becomes a lead in the client database. */}
      <Modal
        isOpen={converting != null}
        onClose={() => (convertSaving ? undefined : setConverting(null))}
        title="Add to client database"
        wide
      >
        {converting && convertDraft ? (
          <>
            <div className="modal-body">
              <p className="page-subtitle">
                {convertEditing
                  ? 'Adjust anything that needs cleaning up, then confirm.'
                  : 'Check the details below before adding this lead. Choose Edit to change anything.'}
              </p>
              {convertError ? (
                <p className="create-order-error" role="alert">
                  {convertError}
                </p>
              ) : null}
              {convertDuplicate ? <DuplicateClientAlert existing={convertDuplicate} /> : null}

              <div className="marketing-order-grid">
                <label className="marketing-order-field">
                  <span>Company *</span>
                  {convertEditing ? (
                    <input
                      type="text"
                      value={convertDraft.company}
                      onChange={(e) => setConvertField('company', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.company || '—'}</p>
                  )}
                </label>
                <label className="marketing-order-field">
                  <span>Point of contact *</span>
                  {convertEditing ? (
                    <input
                      type="text"
                      value={convertDraft.point_of_contact}
                      onChange={(e) => setConvertField('point_of_contact', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.point_of_contact || '—'}</p>
                  )}
                </label>
                <label className="marketing-order-field">
                  <span>Email</span>
                  {convertEditing ? (
                    <input
                      type="email"
                      value={convertDraft.contact_email}
                      onChange={(e) => setConvertField('contact_email', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.contact_email || '—'}</p>
                  )}
                </label>
                <label className="marketing-order-field">
                  <span>Phone</span>
                  {convertEditing ? (
                    <input
                      type="tel"
                      value={convertDraft.contact_phone}
                      onChange={(e) => setConvertField('contact_phone', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.contact_phone || '—'}</p>
                  )}
                </label>
                <label className="marketing-order-field">
                  <span>Source</span>
                  {convertEditing ? (
                    <input
                      type="text"
                      value={convertDraft.source}
                      onChange={(e) => setConvertField('source', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.source || '—'}</p>
                  )}
                </label>
                <label className="marketing-order-field">
                  <span>Industry</span>
                  {convertEditing ? (
                    <input
                      type="text"
                      value={convertDraft.industry}
                      onChange={(e) => setConvertField('industry', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.industry || '—'}</p>
                  )}
                </label>
              </div>
              <label className="marketing-order-field">
                <span>Notes</span>
                {convertEditing ? (
                  <textarea
                    rows={5}
                    value={convertDraft.notes}
                    onChange={(e) => setConvertField('notes', e.target.value)}
                  />
                ) : (
                  <p className="website-lead-message">{convertDraft.notes || '—'}</p>
                )}
              </label>
              <p className="website-lead-muted">Confirming emails the sales team.</p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="cancel-button"
                onClick={() => setConverting(null)}
                disabled={convertSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="website-lead-status-btn"
                onClick={() => setConvertEditing((v) => !v)}
                disabled={convertSaving}
              >
                {convertEditing ? 'Done editing' : 'Edit'}
              </button>
              <button
                type="button"
                className="update-button"
                onClick={() => void confirmConvert()}
                disabled={
                  convertSaving ||
                  !convertDraft.company.trim() ||
                  !convertDraft.point_of_contact.trim()
                }
              >
                {convertSaving ? 'Adding…' : 'Confirm & add lead'}
              </button>
            </div>
          </>
        ) : null}
      </Modal>
    </section>
  );
};

export default BusinessCardScannerSection;
